(() => {
  'use strict';

  if (!document.body.classList.contains('template-collection') || !('IntersectionObserver' in window)) return;

  const wrapperSelector = '[data-wrap-lm]';
  const buttonSelector = '[data-load-more]';
  const currentSelector = '[data-current-bar]';
  const productSelector = '.t4s-product, .t4s-product-item';
  const states = new WeakMap();
  const sentinelStates = new WeakMap();

  let observer;
  let mutationObserver;
  let refreshQueued = false;

  const toWesternDigits = (value) => String(value || '')
    .replace(/[٠-٩]/g, (digit) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(digit)))
    .replace(/[۰-۹]/g, (digit) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(digit)));

  const readCounts = (wrapper) => {
    const label = wrapper.querySelector('.t4s-lm-bar--txt');
    const currentNode = wrapper.querySelector(currentSelector);
    const numbers = toWesternDigits(label?.textContent).match(/\d+/g) || [];
    const current = Number(toWesternDigits(currentNode?.textContent).match(/\d+/)?.[0] || numbers[0] || 0);
    const total = Number(numbers[numbers.length - 1] || current || 0);

    return {
      current: Number.isFinite(current) ? current : 0,
      total: Number.isFinite(total) ? total : 0
    };
  };

  const findGrid = (wrapper) => {
    const scope = wrapper.closest('.t4s-section-inner') || wrapper.closest('section') || document;
    return scope.querySelector('[data-contentlm-replace]') || scope.querySelector('.t4s-products');
  };

  const readProductCount = (wrapper) => {
    const grid = findGrid(wrapper);
    return grid ? new Set(grid.querySelectorAll(productSelector)).size : 0;
  };

  const clearTimer = (state) => {
    if (!state.timer) return;
    window.clearInterval(state.timer);
    state.timer = null;
  };

  const markComplete = (state) => {
    if (state.complete) return;

    clearTimer(state);
    state.complete = true;
    state.loading = false;
    state.wrapper.classList.remove('ts-auto-loading', 'ts-auto-failed');
    state.wrapper.classList.add('ts-auto-complete');
    state.wrapper.removeAttribute('aria-busy');

    const button = state.wrapper.querySelector(buttonSelector);
    if (button) {
      button.removeAttribute('href');
      button.removeAttribute('data-load-onscroll');
      button.setAttribute('aria-disabled', 'true');
    }

    state.fallback.removeAttribute('href');
    observer.unobserve(state.sentinel);
  };

  const setStatus = (state, mode = 'idle') => {
    if (!state.wrapper.isConnected) return;

    const counts = readCounts(state.wrapper);
    const productCount = readProductCount(state.wrapper);
    const shown = counts.total
      ? Math.min(counts.total, Math.max(counts.current, productCount))
      : Math.max(counts.current, productCount);
    const button = state.wrapper.querySelector(buttonSelector);
    const href = button?.getAttribute('href') || '';

    if (href && !state.complete) {
      state.lastHref = href;
      state.fallback.href = href;
    }

    if (state.complete || (counts.total > 0 && shown >= counts.total) || !href) {
      markComplete(state);
      state.count.textContent = counts.total
        ? `تم عرض جميع المنتجات — ${counts.total} منتجًا`
        : 'تم عرض جميع المنتجات';
      state.hint.textContent = '';
      return;
    }

    state.wrapper.classList.remove('ts-auto-complete');
    state.count.textContent = counts.total
      ? `تم عرض ${shown} من ${counts.total} منتجًا`
      : `تم عرض ${shown} منتجًا`;

    if (mode === 'loading') {
      state.hint.textContent = 'جارٍ تحميل المزيد…';
    } else if (mode === 'failed') {
      state.hint.textContent = 'تعذّر التحميل التلقائي؛ يمكن المتابعة من الزر أدناه.';
    } else {
      state.hint.textContent = 'مرّري لمشاهدة المزيد';
    }
  };

  const finishLoading = (state, succeeded, complete = false) => {
    clearTimer(state);

    if (complete) {
      markComplete(state);
      setStatus(state, 'idle');
      return;
    }

    state.loading = false;
    state.wrapper.classList.remove('ts-auto-loading');
    state.wrapper.removeAttribute('aria-busy');

    if (succeeded) {
      state.wrapper.classList.remove('ts-auto-failed');
      setStatus(state, 'idle');

      if (!state.complete) {
        observer.unobserve(state.sentinel);
        window.setTimeout(() => {
          if (state.wrapper.isConnected && !state.complete && state.wrapper.querySelector(`${buttonSelector}[href]`)) {
            observer.observe(state.sentinel);
          }
        }, 1400);
      }
    } else {
      state.wrapper.classList.add('ts-auto-failed');
      if (state.lastHref) state.fallback.href = state.lastHref;
      setStatus(state, 'failed');
    }
  };

  const monitorLoading = (state, before) => {
    const startedAt = Date.now();

    clearTimer(state);
    state.timer = window.setInterval(() => {
      if (!state.wrapper.isConnected) {
        clearTimer(state);
        return;
      }

      const button = state.wrapper.querySelector(buttonSelector);
      const currentHref = button?.getAttribute('href') || '';
      const counts = readCounts(state.wrapper);
      const productCount = readProductCount(state.wrapper);
      const productsAdded = productCount > before.productCount;
      const hrefAdvanced = Boolean(currentHref && currentHref !== before.href);
      const buttonRemoved = !button || !currentHref;
      const countComplete = counts.total > 0 && productCount >= counts.total;

      if (buttonRemoved || countComplete || (productsAdded && !hrefAdvanced)) {
        finishLoading(state, true, true);
      } else if (hrefAdvanced || counts.current !== before.current || productsAdded) {
        finishLoading(state, true);
      } else if (Date.now() - startedAt >= 9000) {
        finishLoading(state, false);
      }
    }, 180);
  };

  function loadNextPage(state) {
    if (!state.wrapper.isConnected || state.loading || state.complete) return;

    const button = state.wrapper.querySelector(buttonSelector);
    const href = button?.getAttribute('href') || '';
    if (!button || !href) {
      markComplete(state);
      setStatus(state, 'idle');
      return;
    }

    if (state.loadedHrefs.has(href)) {
      markComplete(state);
      setStatus(state, 'idle');
      return;
    }

    const counts = readCounts(state.wrapper);
    const before = {
      href,
      current: counts.current,
      productCount: readProductCount(state.wrapper)
    };

    state.loadedHrefs.add(href);
    state.loading = true;
    state.lastHref = href;
    state.wrapper.classList.remove('ts-auto-failed');
    state.wrapper.classList.add('ts-auto-loading');
    state.wrapper.setAttribute('aria-busy', 'true');
    state.fallback.href = href;
    setStatus(state, 'loading');

    button.click();
    monitorLoading(state, before);
  }

  const enhanceWrapper = (wrapper) => {
    const existing = states.get(wrapper);
    if (existing?.sentinel.isConnected && existing?.count.isConnected) {
      setStatus(existing, existing.loading ? 'loading' : 'idle');
      return;
    }

    if (existing) {
      clearTimer(existing);
      observer.unobserve(existing.sentinel);
      sentinelStates.delete(existing.sentinel);
      states.delete(wrapper);
    }

    const nativeButton = wrapper.querySelector(buttonSelector);
    if (!nativeButton) return;

    wrapper.classList.add('ts-auto-enhanced');

    const count = document.createElement('div');
    count.className = 'ts-auto-count';
    count.setAttribute('role', 'status');
    count.setAttribute('aria-live', 'polite');

    const hint = document.createElement('div');
    hint.className = 'ts-auto-hint';

    const fallback = document.createElement('a');
    fallback.className = 'ts-auto-fallback';
    fallback.textContent = 'تحميل المزيد من المنتجات';

    const sentinel = document.createElement('div');
    sentinel.className = 'ts-auto-sentinel';
    sentinel.setAttribute('aria-hidden', 'true');

    const bar = wrapper.querySelector('[data-wrap-lm-bar]');
    if (bar) bar.append(count);
    else wrapper.prepend(count);

    wrapper.append(hint, fallback, sentinel);

    const state = {
      wrapper,
      count,
      hint,
      fallback,
      sentinel,
      lastHref: nativeButton.getAttribute('href') || '',
      loadedHrefs: new Set(),
      loading: false,
      complete: false,
      timer: null
    };

    states.set(wrapper, state);
    sentinelStates.set(sentinel, state);
    observer.observe(sentinel);
    setStatus(state, 'idle');
  };

  const refreshWrappers = () => {
    refreshQueued = false;
    document.querySelectorAll(wrapperSelector).forEach(enhanceWrapper);
  };

  const queueRefresh = () => {
    if (refreshQueued) return;
    refreshQueued = true;
    window.requestAnimationFrame(refreshWrappers);
  };

  const start = () => {
    document.documentElement.classList.add('ts-auto-load-enabled');

    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const state = sentinelStates.get(entry.target);
        if (state) loadNextPage(state);
      });
    }, {
      root: null,
      rootMargin: '0px 0px 500px 0px',
      threshold: 0.01
    });

    refreshWrappers();

    mutationObserver = new MutationObserver(queueRefresh);
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  };

  const startAfterTheme = () => window.setTimeout(start, 900);

  if (document.readyState === 'complete') startAfterTheme();
  else window.addEventListener('load', startAfterTheme, { once: true });
})();
